import { useState, useEffect } from 'react';
import {
  MapContainer, TileLayer,
  Polygon, Polyline, CircleMarker,
  useMapEvents, useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Rubber-band preview line while drawing
function DrawingLayer({ isDrawing, points, onAddPoint }) {
  const [mousePos, setMousePos] = useState(null);

  useMapEvents({
    click(e) {
      if (isDrawing) onAddPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
    mousemove(e) {
      if (isDrawing) setMousePos([e.latlng.lat, e.latlng.lng]);
    },
  });

  const lastPoint = points.length > 0
    ? [points[points.length - 1].lat, points[points.length - 1].lng]
    : null;

  if (!isDrawing || !lastPoint || !mousePos) return null;
  return (
    <Polyline
      positions={[lastPoint, mousePos]}
      pathOptions={{ color: '#fe3130', dashArray: '6,8', weight: 2, opacity: 0.7 }}
    />
  );
}

// Auto-fit map to pre-existing boundary (edit mode)
function FitBoundsOnMount({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(positions, { padding: [50, 50] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * Shared boundary-drawing form.
 *
 * Props:
 *   initialData  – existing property { name, description, notes,
 *                    is_livestock_staging_area, livestock_staging_notes, boundary } (edit mode)
 *   stepLabel    – badge text, e.g. "Step 1 of 2" or "Edit: Step 1 of 2"
 *   saveLabel    – primary button text
 *   cancelLabel  – cancel/back button text
 *   onSave(property)  – called with { name, description, notes,
 *                    is_livestock_staging_area, livestock_staging_notes, boundary }
 *   onCancel()        – called when user cancels
 */
export default function PropertyBoundaryForm({
  initialData = null,
  stepLabel   = 'Step 1 of 2',
  saveLabel   = 'Save Property',
  cancelLabel = 'Cancel',
  onSave,
  onCancel,
}) {
  const existing = initialData?.boundary ?? [];

  const [propertyName,       setPropertyName]       = useState(initialData?.name                       ?? '');
  const [description,        setDescription]        = useState(initialData?.description                ?? '');
  const [notes,              setNotes]              = useState(initialData?.notes                      ?? '');
  const [isStagingArea,      setIsStagingArea]      = useState(initialData?.is_livestock_staging_area   ?? false);
  const [stagingNotes,       setStagingNotes]       = useState(initialData?.livestock_staging_notes     ?? '');
  const [points,             setPoints]             = useState(existing);
  const [isDrawing,    setIsDrawing]    = useState(false);
  const [isFinished,   setIsFinished]   = useState(existing.length >= 3);

  const positions = points.map(p => [p.lat, p.lng]);
  const canFinish = points.length >= 3;
  const canSave   = propertyName.trim() && isFinished;

  const handleAddPoint     = (pt) => setPoints(prev => [...prev, pt]);
  const handleStartDrawing = ()   => { setPoints([]); setIsFinished(false); setIsDrawing(true); };
  const handleUndoLast     = ()   => setPoints(prev => prev.slice(0, -1));
  const handleCancel       = ()   => { setPoints(existing); setIsDrawing(false); setIsFinished(existing.length >= 3); };
  const handleFinish       = ()   => { if (canFinish) { setIsDrawing(false); setIsFinished(true); } };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: propertyName,
      description,
      notes,
      is_livestock_staging_area: isStagingArea,
      livestock_staging_notes: stagingNotes,
      boundary: points,
    });
  };

  const mapHint = !isDrawing && !isFinished
    ? 'Click "Start Drawing" then click on the map to trace your property boundary.'
    : isDrawing
      ? `${points.length} point${points.length !== 1 ? 's' : ''} placed — keep clicking to add vertices.`
      : `Boundary complete (${points.length} points). Click "Redraw" to start over.`;

  return (
    <form onSubmit={handleSubmit} className="property-form">

      {stepLabel && <span className="step-badge-inline">{stepLabel}</span>}

      <div className="form-group">
        <label htmlFor="propertyName">Farm / Ranch Name <span className="required">*</span></label>
        <input
          type="text" id="propertyName"
          value={propertyName}
          onChange={e => setPropertyName(e.target.value)}
          placeholder="e.g., Bar-HH Ranch"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description <span className="optional">(optional)</span></label>
        Include any information about the nature of your operation.
        <textarea
          id="description" rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Brief description of your operation..."
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notes for Emergency Personnel <span className="optional">(optional)</span></label>
        This information will be made available to emergency responders to help them assist you in case of an emergency.
        <textarea
          id="notes" rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add your notes here"
        />
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={isStagingArea}
            onChange={e => setIsStagingArea(e.target.checked)}
          />
          This property can be used as a <strong>livestock staging area</strong> during an emergency
        </label>
        <p className="field-hint">
          If checked, other users may see this land as a place to send or house animals evacuated from nearby properties.
        </p>
      </div>

      {isStagingArea && (
        <div className="form-group">
          <label htmlFor="stagingNotes">Livestock staging details <span className="optional">(optional)</span></label>
          <p className="field-hint">
            Describe the facility (corrals, barns, pasture, water), and how many and what types of livestock you can house for others in an emergency.
          </p>
          <textarea
            id="stagingNotes"
            rows={4}
            value={stagingNotes}
            onChange={e => setStagingNotes(e.target.value)}
            placeholder="e.g. 40 acres of cross-fenced pasture with a stock tank; can hold up to 100 head of cattle or 50 horses for several days."
          />
        </div>
      )}

      <div className="form-group">
        <label>Property Boundary <span className="required">*</span></label>
        <p className="map-hint">{mapHint}</p>

        <div className="map-toolbar">
          {!isDrawing && !isFinished && (
            <button type="button" className="map-btn draw" onClick={handleStartDrawing}>✏️ Start Drawing</button>
          )}
          {isDrawing && (<>
            <button type="button" className="map-btn finish" onClick={handleFinish} disabled={!canFinish}>✓ Finish ({points.length} pts)</button>
            <button type="button" className="map-btn undo"   onClick={handleUndoLast} disabled={points.length === 0}>↩ Undo</button>
            <button type="button" className="map-btn cancel" onClick={handleCancel}>✕ Cancel</button>
          </>)}
          {isFinished && (
            <button type="button" className="map-btn draw" onClick={handleStartDrawing}>↺ Redraw</button>
          )}
        </div>

        <div className={`map-wrapper${isDrawing ? ' drawing-active' : ''}`}>
          <MapContainer center={[39.5, -98.35]} zoom={4} style={{ height: '420px', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {/* Auto-fit to existing boundary when editing */}
            {existing.length >= 2 && <FitBoundsOnMount positions={existing.map(p => [p.lat, p.lng])} />}
            <DrawingLayer isDrawing={isDrawing} points={points} onAddPoint={handleAddPoint} />
            {points.map((pt, i) => (
              <CircleMarker key={i} center={[pt.lat, pt.lng]} radius={5}
                pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 1, weight: 2 }}
              />
            ))}
            {!isFinished && positions.length >= 2 && (
              <Polyline positions={positions} pathOptions={{ color: '#fe3130', weight: 2 }} />
            )}
            {isFinished && positions.length >= 3 && (
              <Polygon positions={positions}
                pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 0.2, weight: 2 }} />
            )}
          </MapContainer>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-save" disabled={!canSave}>{saveLabel}</button>
        <button type="button" className="btn-skip" onClick={onCancel}>{cancelLabel}</button>
      </div>

    </form>
  );
}

